import type { OperationsFlagged, OperationsTab } from '@ops/shared/model';
import { OPERATIONS_FLAGGED, OPERATIONS_TAB } from '@ops/shared/model';
import { operationSortFields } from '@travel-planner/shared/schemas/operations';

export type OperationsSortField = (typeof operationSortFields)[number];
export type OperationsSortOrder = 'asc' | 'desc';

// Round-trip URL search params <-> typed filter object for /operations.
// Used by the route's `validateSearch` and consumed via `useSearch` in the
// operations layout. Agent-targetable filters + deep-linkable toolbar bullets
// (`flagged` for XT-90 / TCO-OPS06-002) live here; view toggle / sub-tab chips
// remain in `useOperationsFilterStore` (Zustand).

const SEARCH_FIELD_VALUES = [
  'optigest',
  'traveler',
  'customer',
  'supplier',
] as const;

export type OperationsSearchField = (typeof SEARCH_FIELD_VALUES)[number];

// Page sizes offered by the operations table's "Rows per page" selector. Shared
// so the URL `limit` whitelist below and the selector options can't drift apart.
export const OPERATIONS_PAGE_SIZES = [20, 40, 60, 100] as const;

export type OperationsSearch = {
  // Already URL-truth at the route level. Carried through every validator
  // call so the existing tab navigation (`OperationsLayout`) keeps working.
  tab?: OperationsTab;
  /**
   * Selected XT-380 filter-card id for the active tab. Overlays its preset onto
   * the list query (shareable / refresh-safe). Cleared on tab change.
   */
  opsCard?: string;
  // Migrated from store — agent-targetable.
  search?: string;
  searchField?: OperationsSearchField;
  startDate?: string;
  endDate?: string;
  itineraries?: string[];
  contentCategory?: string[];
  country?: string[];
  itineraryPriorityLevel?: string[];
  operationStatuses?: string[];
  // Paging — mirrors products' `page` URL key (filter changes reset to undefined).
  offset?: number;
  limit?: number;
  // Column sort — URL-resident so it survives refresh and is shareable.
  sortBy?: OperationsSortField;
  sortOrder?: OperationsSortOrder;
  /**
   * Flagged surface deep-link (TCO-OPS06-002 / XT-90). Mirrors the toolbar
   * bullets so Dashboard "Flagged" links open the Flagged view:
   * `/operations?flagged=flagged` | `preFlagged` | `failedMsg`.
   */
  flagged?: OperationsFlagged;
};

const TAB_VALUES: ReadonlyArray<OperationsTab> = Object.values(OPERATIONS_TAB);

const asTab = (v: unknown): OperationsTab | undefined =>
  typeof v === 'string' && (TAB_VALUES as ReadonlyArray<string>).includes(v)
    ? (v as OperationsTab)
    : undefined;

const asStringArray = (v: unknown): string[] | undefined =>
  Array.isArray(v)
    ? v.filter((x): x is string => typeof x === 'string')
    : undefined;

const asSearchField = (v: unknown): OperationsSearchField | undefined =>
  typeof v === 'string' &&
  (SEARCH_FIELD_VALUES as ReadonlyArray<string>).includes(v)
    ? (v as OperationsSearchField)
    : undefined;

const asSortBy = (v: unknown): OperationsSortField | undefined =>
  typeof v === 'string' &&
  (operationSortFields as ReadonlyArray<string>).includes(v)
    ? (v as OperationsSortField)
    : undefined;

const asSortOrder = (v: unknown): OperationsSortOrder | undefined =>
  v === 'asc' || v === 'desc' ? v : undefined;

const FLAGGED_VALUES: ReadonlyArray<OperationsFlagged> =
  Object.values(OPERATIONS_FLAGGED);

const asFlagged = (v: unknown): OperationsFlagged | undefined =>
  typeof v === 'string' && (FLAGGED_VALUES as ReadonlyArray<string>).includes(v)
    ? (v as OperationsFlagged)
    : undefined;

// Accept either a bare `YYYY-MM-DD` or the full UTC ISO timestamp the shared
// `DateRangePicker` emits (`2026-05-25T00:00:00.000Z`). Both round-trip
// unchanged; anything else (partials, `tomorrow`, slashes) is rejected.
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATETIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/;

const asDateString = (v: unknown): string | undefined =>
  typeof v === 'string' &&
  (DATE_PATTERN.test(v) || ISO_DATETIME_PATTERN.test(v))
    ? v
    : undefined;

// `search` is free text, but a purely-numeric value (e.g. an OptiGest ref like
// "111") is parsed out of the URL as a number — coerce it back to a string so
// it survives validation and round-trips.
const asSearchString = (v: unknown): string | undefined => {
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') return v || undefined;
  return undefined;
};

export const validateOperationsSearch = (
  input: Record<string, unknown>
): OperationsSearch => {
  // Legacy deep-link params from OptiGest / the planner: `?ref=<optigest>`
  // (search by OptiGest reference) and `?itineraryRef=<itineraryId>` (preselect
  // that itinerary). Translate them into the canonical search shape so external
  // links keep working; an explicit `search`/`itineraries` always wins.
  const ref = asSearchString(input.ref);
  const itineraryRef =
    typeof input.itineraryRef === 'string' && input.itineraryRef
      ? input.itineraryRef
      : undefined;

  // The toolbar's search-field `Select` defaults to "Optigest" visually but
  // only writes `searchField` to the URL when the user actively changes it —
  // typing in the box alone produces `?search=<term>` with no `searchField`.
  // The backend skips the filter unless BOTH are present, so default the field
  // to `optigest` whenever there's a search term (mirroring the dropdown's
  // default), not only for the legacy `?ref=` deep link.
  const searchValue = asSearchString(input.search) ?? ref;

  return {
    tab: asTab(input.tab),
    opsCard: typeof input.opsCard === 'string' ? input.opsCard : undefined,
    search: searchValue,
    searchField:
      asSearchField(input.searchField) ??
      (searchValue ? 'optigest' : undefined),
    startDate: asDateString(input.startDate),
    endDate: asDateString(input.endDate),
    itineraries:
      asStringArray(input.itineraries) ??
      (itineraryRef ? [itineraryRef] : undefined),
    contentCategory: asStringArray(input.contentCategory),
    country: asStringArray(input.country),
    itineraryPriorityLevel: asStringArray(input.itineraryPriorityLevel),
    operationStatuses: asStringArray(input.operationStatuses),
    offset: typeof input.offset === 'number' ? input.offset : undefined,
    limit:
      typeof input.limit === 'number' &&
      (OPERATIONS_PAGE_SIZES as readonly number[]).includes(input.limit)
        ? input.limit
        : undefined,
    sortBy: asSortBy(input.sortBy),
    sortOrder: asSortOrder(input.sortOrder),
    flagged: asFlagged(input.flagged),
  };
};
